import Link from '@eliadmin/core/src/components/link';

import type { Faq } from '@eliadmin/core/src/api/faqs/faqs-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../shared/heading';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { HtmlDescription } from '@eliadmin/core/src/components/ui/html-description';
import { LuMinus, LuPlus } from 'react-icons/lu';
import { Reveal } from '../ui/reveal';

export type FAQProps = {
  faq: Faq[];
  className?: { section?: string };
  headingAs?: 'h1' | 'h2';
  /** Compatibility: all answers always remain in SSR HTML. */
  renderAllAnswers?: boolean;
};

export function FAQView({
  faq,
  className,
  headingAs = 'h2',
  labels,
  disclosureGroup,
}: FAQProps & {
  labels: { title: string; description: string; contact: string };
  disclosureGroup: string;
}) {
  return (
    <section
      id="faq"
      className={cn(
        'custom-section',
        'pb-12 pt-6 sm:pb-14 sm:pt-8 md:pb-20 md:pt-10 lg:pb-24 lg:pt-12',
        className?.section,
      )}
    >
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 md:flex-row md:justify-between md:gap-8 lg:gap-12',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-6">
          <Reveal>
            <Heading subtitle="FAQ" title={labels.title} as={headingAs} />
          </Reveal>
          <Reveal>
            <p className="hidden text-sm font-light text-black/70 sm:block sm:text-base">
              {labels.description}
            </p>
          </Reveal>

          <Button asChild className="hidden sm:flex">
            <Link prefetchOnHover href={ROUTES.CONTACT()}>
              {labels.contact}
            </Link>
          </Button>
        </div>
        <div
          className={cn('flex w-full flex-col items-start justify-start gap-2 md:max-w-[641px]')}
        >
          {faq.map(item => (
            <Reveal key={item.id}>
              <details
                name={disclosureGroup}
                className="ea-faq__native group/faq w-full border-b border-t-0 border-b-black/5 px-0 py-4"
              >
                <summary
                  className={cn(
                    'flex w-full items-center justify-between p-0 text-left text-base font-medium hover:no-underline sm:text-lg',
                    'ea-faq__accordion-trigger cursor-pointer list-none group-open/faq:text-primary [&::-webkit-details-marker]:hidden',
                  )}
                >
                  {item.question}
                  <span aria-hidden="true" className="shrink-0 group-open/faq:hidden">
                    <LuPlus className="text-black" size={20} />
                  </span>
                  <span aria-hidden="true" className="hidden shrink-0 group-open/faq:block">
                    <LuMinus className="text-primary" size={20} />
                  </span>
                </summary>
                <div className="p-0 pt-4">
                  <HtmlDescription
                    sectionContent
                    imagePolicy={{
                      loading: 'lazy',
                      decoding: 'async',
                      sizes: '(min-width: 768px) 641px, calc(100vw - 48px)',
                    }}
                    color="foreground"
                    content={item.answer}
                    size="sm"
                    className={cn('w-full', 'ea-faq__content')}
                  />
                </div>
              </details>
            </Reveal>
          ))}
        </div>
        <p className="block text-sm font-light text-black/70 sm:hidden sm:text-base">
          {labels.description}
        </p>
        <Reveal className="flex sm:hidden">
          <Button asChild className="flex sm:hidden">
            <Link prefetchOnHover href={ROUTES.CONTACT()}>
              {labels.contact}
            </Link>
          </Button>
        </Reveal>
      </div>
    </section>
  );
}
